<template>
  <v-expansion-panels v-model="expandedPanels" multiple>
    <!-- URL Panel – shows all URLs for pages -->
    <v-expansion-panel>
      <v-expansion-panel-title>
        Page URLs ({{ urlFilters.displayedUrls.value.length }})
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-row>
          <v-col cols="12">
           <!-- Use the common URL filter chips including sitemap filters -->
           <UrlFilterChips
              :fetch-status-counts="urlFilters.fetchStatusCounts.value"
              :active-fetch-status-filters="urlFilters.activeFetchStatusFilters.value"
              :toggle-fetch-status-filter="urlFilters.toggleFetchStatusFilter"
              show-sitemap-filters
              :sitemap-count="urlFilters.sitemapCount.value"
              :non-sitemap-count="urlFilters.nonSitemapCount.value"
              :active-sitemap-filters="urlFilters.activeSitemapFilters.value"
              :toggle-sitemap-filter="urlFilters.toggleSitemapFilter"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="url in urlFilters.displayedUrls.value"
            :key="url.pathname"
            cols="12"
            sm="6"
            md="4"
          >
            <UrlCard :item="url"/>
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>

    <!-- Processed Pages Panel -->
    <v-expansion-panel v-if="filteredPages.length > 0">
      <v-expansion-panel-title>
        Processed Pages ({{ filteredPages.length }})
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-row>
          <v-col cols="12">
            <div class="d-flex flex-wrap gap-2 mb-3">
              <v-chip
                v-if="pagesStore.missingMetaTitleCount > 0"
                :color="activeFilters.includes('metaTitle') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('metaTitle')"
              >
                Missing Meta Title ({{ pagesStore.missingMetaTitleCount }})
              </v-chip>
              <v-chip
                v-if="pagesStore.missingMetaDescriptionCount > 0"
                :color="activeFilters.includes('metaDescription') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('metaDescription')"
              >
                Missing Meta Description ({{ pagesStore.missingMetaDescriptionCount }})
              </v-chip>
              <v-chip
                v-if="pagesStore.missingDescriptionCount > 0"
                :color="activeFilters.includes('description') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('description')"
              >
                Missing Description ({{ pagesStore.missingDescriptionCount }})
              </v-chip>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="page in filteredPages"
            :key="page.pathname"
            cols="12"
            md="4"
          >
            <PageItem :page="page" />
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { usePagesStore } from '@/store/pagesStore';
import PageItem from './PageItem.vue';
import UrlCard from './UrlCard.vue';
import UrlTypes from '@/enums/UrlTypes';
import { useActiveFilters } from '@/composables/useActiveFilters';
import { applyMissingFilters } from '@/helpers/filterMissing';
import UrlFilterChips from '@/components/UrlFilterChips.vue';
import { useUrlFilters } from '@/composables/useUrlFilters';

const pagesStore = usePagesStore();
const { activeFilters, toggleFilter } = useActiveFilters();

const filteredPages = computed(() =>
  applyMissingFilters(pagesStore.pages, activeFilters.value, (page, filter) => {
    if (filter === 'metaTitle') return !page.metaTitle;
    if (filter === 'metaDescription') return !page.metaDescription;
    if (filter === 'description') return !page.description.descriptionHtml;
    return true;
  })
);

// Use the new composable for URL filtering.
const urlFilters = useUrlFilters((url) => url.urlType === UrlTypes.PAGE);

// Expand processed panel by default (panel index 1)
const expandedPanels = ref([1]);
</script>
